// Phase P — Plan display helpers.
//
// Single source of truth for pricing-surface rendering. The <PriceDisplay>
// component renders the correct combination of:
//   * regular price (strikethrough) when set → "was €49"
//   * current price (bold) → "€40"
//   * promo pill next to the price → "Special offer"
//   * "+ VAT" suffix (muted, small) unless the plan explicitly opts out
//     via vat_suffix=false (trial + custom).
//
// Consumers can either:
//   (a) pass an explicit `plan` object matching the /api/plan/display shape
//   (b) pass a `code` string and let the component pull from usePlanDisplay
//
// The hook caches the /api/plan/display response for the session — this
// endpoint is public + immutable per deploy so a single fetch is enough.

import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const PlanDisplayContext = createContext(null);

// Fallback catalog — used before the network fetch resolves, and as a
// safety net if the API call fails. Matches the backend PLAN_DISPLAY dict
// byte-for-byte so SSR/first-paint doesn't jitter.
export const FALLBACK_PLAN_DISPLAY = {
  basic:         { name: "JobsBook Basic",       price: "€29",  regular_price: null,  cadence: "month",  promo_label: null,             vat_suffix: true },
  pro:           { name: "JobsBook Pro",         price: "€40",  regular_price: "€49", cadence: "month",  promo_label: "Special offer",  vat_suffix: true },
  pro_annual:    { name: "JobsBook Pro Annual",  price: "€399", regular_price: null,  cadence: "year",   promo_label: null,             vat_suffix: true },
  pro_unlimited: { name: "JobsBook Pro Max",     price: "€69",  regular_price: null,  cadence: "month",  promo_label: null,             vat_suffix: true },
  trial:         { name: "Trial",                price: "—",    regular_price: null,  cadence: "trial",  promo_label: null,             vat_suffix: false },
  custom:        { name: "Custom",               price: "—",    regular_price: null,  cadence: "custom", promo_label: null,             vat_suffix: false },
};

export const VAT_FOOTNOTE = "Prices exclude VAT · billed in EUR";

export function PlanDisplayProvider({ children }) {
  const [catalog, setCatalog] = useState(FALLBACK_PLAN_DISPLAY);
  const [footnote, setFootnote] = useState(VAT_FOOTNOTE);
  useEffect(() => {
    // Public endpoint — no auth needed. Fetch once per app boot.
    api.get("/plan/display")
      .then(({ data }) => {
        if (data?.plans) setCatalog(data.plans);
        if (data?.vat_footnote) setFootnote(data.vat_footnote);
      })
      .catch(() => { /* Fallback catalog already in state. */ });
  }, []);
  return (
    <PlanDisplayContext.Provider value={{ catalog, footnote }}>
      {children}
    </PlanDisplayContext.Provider>
  );
}

export function usePlanDisplay() {
  return useContext(PlanDisplayContext) || { catalog: FALLBACK_PLAN_DISPLAY, footnote: VAT_FOOTNOTE };
}

// Cadence suffix for the price. `/month`, `/year`, or blank for trial/custom.
function cadenceSuffix(cadence) {
  if (cadence === "month") return "/month";
  if (cadence === "year")  return "/year";
  return "";
}

/**
 * <PriceDisplay code="pro" size="lg" />
 * or
 * <PriceDisplay plan={{ price: "€40", regular_price: "€49", promo_label: "Special offer", vat_suffix: true, cadence: "month" }} />
 *
 * Size variants control the type scale so the same helper works in a big
 * plan card and inside a small dropdown label.
 */
export function PriceDisplay({ code, plan, size = "md", hideCadence = false, className = "" }) {
  const { catalog } = usePlanDisplay();
  const p = plan || catalog[code] || catalog.custom;
  if (!p) return null;
  const priceCls =
    size === "lg" ? "text-2xl font-black" :
    size === "sm" ? "text-sm font-semibold" :
    "text-base font-bold";
  const regularCls =
    size === "lg" ? "text-base line-through text-muted-foreground mr-1.5" :
    size === "sm" ? "text-[11px] line-through text-muted-foreground mr-1" :
    "text-xs line-through text-muted-foreground mr-1.5";
  const suffixCls =
    size === "lg" ? "text-xs text-muted-foreground ml-1" :
    "text-[10px] text-muted-foreground ml-1";
  return (
    <span className={`inline-flex items-baseline gap-1 flex-wrap ${className}`} data-testid={`price-display-${code || "custom"}`}>
      {p.regular_price && (
        <span className={regularCls} data-testid="price-regular">{p.regular_price}</span>
      )}
      <span className={priceCls} data-testid="price-current">{p.price}</span>
      {!hideCadence && cadenceSuffix(p.cadence) && (
        <span className={suffixCls}>{cadenceSuffix(p.cadence)}</span>
      )}
      {p.vat_suffix && p.price !== "—" && (
        <span className={suffixCls} data-testid="price-vat-suffix">+ VAT</span>
      )}
      {p.promo_label && (
        <span
          className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 ml-1"
          data-testid="price-promo-label"
        >
          {p.promo_label}
        </span>
      )}
    </span>
  );
}

// Concise one-liner used inside <Select> options and dropdown labels where
// stacking + pills would break the row height. Renders "€40 (was €49) +VAT".
export function priceInline(code, catalog) {
  const p = (catalog || FALLBACK_PLAN_DISPLAY)[code] || FALLBACK_PLAN_DISPLAY.custom;
  const bits = [p.price];
  if (p.regular_price) bits.push(`(was ${p.regular_price})`);
  if (p.vat_suffix && p.price !== "—") bits.push("+ VAT");
  if (p.cadence === "month") bits.push("· monthly");
  else if (p.cadence === "year") bits.push("· annual");
  return bits.join(" ");
}

// Small unstyled component for pricing-surface footnotes.
export function VatFootnote({ className = "" }) {
  const { footnote } = usePlanDisplay();
  return (
    <div
      className={`text-[11px] text-muted-foreground mt-3 ${className}`}
      data-testid="pricing-vat-footnote"
    >
      {footnote}
    </div>
  );
}
